import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { Container, Table, Button, Alert, Row, Col, Nav, Badge, Modal, ListGroup } from 'react-bootstrap';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';
import UserAvatar from "./components/UserAvatar";

const AttendingEvaluations = () => {
  const [assignedEvals, setAssignedEvals] = useState([]);
  const [completedEvals, setCompletedEvals] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [selectedRecurringEvaluation, setSelectedRecurringEvaluation] = useState(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("attendingEvaluationsTab") || "assigned";
  });

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("attendingEvaluationsTab", tab);
  };

  // Group recurring evaluations by template and resident
  const groupRecurringEvaluations = (evaluations) => {
    console.log("Grouping evaluations:", evaluations);

    // Group by templateId AND residentId to separate evaluations for each resident
    const groupedByTemplateAndResident = evaluations.reduce((acc, evaluation) => {
      if (evaluation.templateId && evaluation.residentId) {
        const key = `template-${evaluation.templateId}-resident-${evaluation.residentId}`;
        if (!acc[key]) {
          acc[key] = {
            id: key,
            title: evaluation.title,
            evaluationType: evaluation.evaluationType,
            resident: evaluation.resident,
            templateId: evaluation.templateId,
            residentId: evaluation.residentId,
            evaluations: [],
          };
        }
        acc[key].evaluations.push(evaluation);
      } else {
        // For evaluations without templateId or residentId, keep them as single entries
        acc[`single-${evaluation.id}`] = {
          id: evaluation.id,
          title: evaluation.title,
          evaluationType: evaluation.evaluationType,
          resident: evaluation.resident,
          dueDate: evaluation.dueDate,
          status: evaluation.status,
          responseId: evaluation.responseId,
          isSingle: true,
          evaluations: [], // Add empty evaluations array for consistency
        };
      }
      return acc;
    }, {});

    console.log("Grouped by template and resident:", groupedByTemplateAndResident);

    // Convert to array and sort evaluations within each group by due date
    const result = Object.values(groupedByTemplateAndResident).map(group => {
      console.log(`Processing group: ${group.id}, type: ${group.evaluationType}, evaluations count: ${group.evaluations.length}`);
      
      // For feedback evaluations with multiple evaluations, update the title
      if (group.evaluationType === 'feedback' && group.evaluations && group.evaluations.length > 1) {
        const residentName = group.resident ? `${group.resident.firstName} ${group.resident.lastName}` : 'Unknown Resident';
        console.log(`Updating title for feedback group with ${group.evaluations.length} evaluations for ${residentName}`);
        return {
          ...group,
          title: `Procedure Feedbacks for ${residentName}`,
          evaluations: (group.evaluations || []).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        };
      }
      
      return {
        ...group,
        evaluations: (group.evaluations || []).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      };
    });

    console.log("Grouped evaluations result:", result);
    return result;
  };

  // Recurring Evaluations Modal Component
  const RecurringEvaluationsModal = ({ show, handleClose, evaluation }) => {
    const [unlockingEvaluation, setUnlockingEvaluation] = useState(null);
    const userRole = localStorage.getItem('role');
    const isDirector = userRole === 'director';

    if (!evaluation) return null;

    const handleUnlockEvaluation = async (evaluationId) => {
      setUnlockingEvaluation(evaluationId);
      try {
        const response = await fetch(`${API_ENDPOINTS.DIRECTOR}/unlock-evaluation/${evaluationId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          alert(`Evaluation unlocked successfully! ${result.message}`);
          // Refresh the page to show updated status
          window.location.reload();
        } else {
          const error = await response.json();
          alert(error.message || 'Failed to unlock evaluation');
        }
      } catch (error) {
        console.error('Error unlocking evaluation:', error);
        alert('Failed to unlock evaluation');
      } finally {
        setUnlockingEvaluation(null);
      }
    };

    const residentName = evaluation.resident
      ? `${evaluation.resident.firstName} ${evaluation.resident.lastName}`.trim()
      : "Resident";

    return (
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <div className="d-flex flex-column">
            <h6 className="mb-0 fw-semibold">Recurring Evaluations for</h6>
            <div className="d-flex align-items-center gap-1">
              <UserAvatar user={evaluation.resident} size={24} alt="" />
              <span className="fw-semibold">{residentName}</span>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <h6 className="mb-3">{evaluation.title}</h6>
          <ListGroup>
            {(evaluation.evaluations || []).map((subEval) => {
              const isLocked = subEval.startTime && new Date(subEval.startTime) > new Date();
              const isCompleted = subEval.status === "completed";
              
              return (
                <ListGroup.Item key={subEval.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold mb-1">
                      {subEval.title || "Evaluation"}
                    </div>
                    <div className="text-muted small mb-1">
                      Created: {subEval.createdAt ? new Date(subEval.createdAt).toLocaleDateString() : "Unknown"}
                    </div>
                    <div className="text-muted small">
                      Due: {subEval.dueDate ? new Date(subEval.dueDate).toLocaleDateString() : "Not set"}
                    </div>
                    <Badge bg={isCompleted ? "success" : isLocked ? "secondary" : "warning"} className="mt-1">
                      {isCompleted ? "Completed" : isLocked ? "Locked" : "Pending"}
                    </Badge>
                  </div>
                  <div className="d-flex gap-2">
                    {isDirector && isLocked && !isCompleted && (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleUnlockEvaluation(subEval.id)}
                        disabled={unlockingEvaluation === subEval.id}
                      >
                        {unlockingEvaluation === subEval.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Unlocking...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-unlock me-1"></i>
                            Unlock
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        handleClose();
                        if (isCompleted) {
                          navigate(`/attending/completed-evaluation/${subEval.responseId}`);
                        } else {
                          navigate(`/evaluations/${subEval.id}/complete`);
                        }
                      }}
                      disabled={isLocked}
                    >
                      {isCompleted 
                        ? "View" 
                        : isLocked
                          ? `Available ${new Date(subEval.startTime).toLocaleDateString()}`
                          : "Complete"}
                    </Button>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/");
          return;
        }

        // Fetch assigned evaluations
        const assignedRes = await fetch(`${API_ENDPOINTS.ATTENDING}/assigned`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!assignedRes.ok) {
          throw new Error(`HTTP error! status: ${assignedRes.status}`);
        }

        const assignedData = await assignedRes.json();
        setAssignedEvals(assignedData);
        
        // Fetch completed evaluations
        const completedRes = await fetch(`${API_ENDPOINTS.ATTENDING}/completed`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!completedRes.ok) {
          throw new Error(`HTTP error! status: ${completedRes.status}`);
        }

        const completedData = await completedRes.json();
        setCompletedEvals(completedData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching evaluations:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchEvaluations();
  }, []);

  if (loading) {
    return (
      <div>
        <Navbar pageTitle="Attending Evaluations" />
        <Container className="py-4">
          <div className="text-center">
            <h4>Loading...</h4>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Navbar pageTitle="Attending Evaluations" />
        <Container className="py-4">
          <Alert variant="danger">Error: {error}</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Attending Evaluations" description="Manage evaluations assigned to attending physicians" />
      <Navbar 
        pageTitle="Attending Evaluations" 
      />
      
      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col lg={2} md={2} sm={12} className="d-flex align-items-center justify-content-start">
              <h5 className="mb-0 py-3">Evaluations</h5>
            </Col>
            <Col lg={10} md={10} sm={12} className="d-flex align-items-center justify-content-start">
              <Nav variant="tabs" style={{marginBottom: '-1px'}} activeKey={activeTab} onSelect={handleTabChange}>
                <Nav.Item>
                  <Nav.Link eventKey="assigned" active={activeTab === 'assigned'}>Assigned to Me</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="completed" active={activeTab === 'completed'}>Completed</Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-4">
        {activeTab === 'assigned' && (
          <div>
            <h4 className="mb-3">Assigned Evaluations</h4>
            {assignedEvals.length === 0 ? (
              <Alert variant="info">No evaluations assigned to you yet.</Alert>
            ) : (
              <Table hover responsive>
                <thead>
                  <tr>
                    <th className="px-4">Evaluation</th>
                    <th>Type</th>
                    <th>Recurrence</th>
                    <th>Resident</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRecurringEvaluations(assignedEvals).map((evaluation) => (
                    <tr key={evaluation.id}>
                      <td className="px-4">{evaluation.title}</td>
                      <td>
                        <Badge
                          bg={
                            evaluation.evaluationType === 'performance'
                              ? 'primary'
                              : evaluation.evaluationType === 'feedback'
                                ? 'info'
                                : evaluation.evaluationType === 'survey'
                                  ? 'success'
                                  : 'warning'
                          }
                        >
                          {evaluation.evaluationType === 'performance'
                            ? 'Performance'
                            : evaluation.evaluationType === 'feedback'
                              ? 'Feedback'
                              : evaluation.evaluationType === 'survey'
                                ? 'Survey'
                                : 'Questionnaire'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg="secondary">
                          {evaluation.isSingle 
                            ? "Non-recurring" 
                            : (evaluation.evaluations && evaluation.evaluations.length === 1)
                              ? "Single evaluation"
                              : `Recurring (${evaluation.evaluations ? evaluation.evaluations.length : 0} total)`}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <UserAvatar user={evaluation.resident} size={32} alt="" />
                          <span>
                            {evaluation.resident?.firstName} {evaluation.resident?.lastName}
                          </span>
                        </div>
                      </td>
                      <td>
                        {evaluation.isSingle
                          ? evaluation.dueDate
                            ? new Date(evaluation.dueDate).toLocaleDateString()
                            : "Not set"
                          : (evaluation.evaluations && evaluation.evaluations.length > 0)
                            ? new Date(evaluation.evaluations[0].dueDate).toLocaleDateString()
                            : "Not set"}
                      </td>
                      <td>
                        {evaluation.isSingle ? (
                          <Badge bg={evaluation.status === "completed" ? "success" : "warning"}>
                            {evaluation.status === "completed" ? "Completed" : "Pending"}
                          </Badge>
                        ) : (
                          <Badge bg={(evaluation.evaluations && evaluation.evaluations.some((e) => e.status === "pending")) ? "warning" : "success"}>
                            {(evaluation.evaluations && evaluation.evaluations.some((e) => e.status === "pending")) ? "Some Pending" : "All Completed"}
                          </Badge>
                        )}
                      </td>
                      <td>
                        {evaluation.isSingle ? (
                          <Button
                            variant="primary"
                            onClick={() => navigate(`/evaluations/${evaluation.id}/complete`)}
                            disabled={evaluation.startTime && new Date(evaluation.startTime) > new Date()}
                          >
                            {evaluation.startTime && new Date(evaluation.startTime) > new Date() 
                              ? `Available ${new Date(evaluation.startTime).toLocaleDateString()}`
                              : "Complete Evaluation"}
                          </Button>
                        ) : (evaluation.evaluations && evaluation.evaluations.length === 1) ? (
                          <Button
                            variant="primary"
                            onClick={() => navigate(`/evaluations/${evaluation.evaluations[0].id}/complete`)}
                            disabled={evaluation.evaluations[0].startTime && new Date(evaluation.evaluations[0].startTime) > new Date()}
                          >
                            {evaluation.evaluations[0].startTime && new Date(evaluation.evaluations[0].startTime) > new Date()
                              ? `Available ${new Date(evaluation.evaluations[0].startTime).toLocaleDateString()}`
                              : "Complete Evaluation"}
                          </Button>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSelectedRecurringEvaluation(evaluation);
                              setShowRecurringModal(true);
                            }}
                          >
                            View Details
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}

        {activeTab === 'completed' && (
          <div>
            <h4 className="mb-3">Completed Evaluations</h4>
            {completedEvals.length === 0 ? (
              <Alert variant="info">No completed evaluations yet.</Alert>
            ) : (
              <>
                {/* Performance Evaluations */}
                {completedEvals.filter(evaluation => evaluation.evaluationType === 'performance').length > 0 && (
                  <div className="mb-4">
                    <h5 className="mb-3">Performance Evaluations</h5>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th className="px-4">Title</th>
                          <th>Resident</th>
                          <th>Submitted On</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedEvals
                          .filter(evaluation => evaluation.evaluationType === 'performance')
                          .map((evaluation) => (
                            <tr key={evaluation.id}>
                              <td className="px-4">{evaluation.title}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <UserAvatar user={evaluation.resident} size={32} alt="" />
                                  <span>
                                    {evaluation.resident?.firstName} {evaluation.resident?.lastName}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {evaluation.submittedAt
                                  ? new Date(evaluation.submittedAt).toLocaleString()
                                  : "Date not available"}
                              </td>
                              <td>
                                <Button
                                  variant="primary"
                                  onClick={() => navigate(`/attending/completed-evaluation/${evaluation.responseId}`)}
                                >
                                  View Response
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Feedback Evaluations */}
                {completedEvals.filter(evaluation => evaluation.evaluationType === 'feedback').length > 0 && (
                  <div className="mb-4">
                    <h5 className="mb-3">Feedback Evaluations</h5>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th className="px-4">Title</th>
                          <th>Resident</th>
                          <th>Submitted On</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedEvals
                          .filter(evaluation => evaluation.evaluationType === 'feedback')
                          .map((evaluation) => (
                            <tr key={evaluation.id}>
                              <td className="px-4">{evaluation.title}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <UserAvatar user={evaluation.resident} size={32} alt="" />
                                  <span>
                                    {evaluation.resident?.firstName} {evaluation.resident?.lastName}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {evaluation.submittedAt
                                  ? new Date(evaluation.submittedAt).toLocaleString()
                                  : "Date not available"}
                              </td>
                              <td>
                                <Button
                                  variant="primary"
                                  onClick={() => navigate(`/attending/completed-evaluation/${evaluation.responseId}`)}
                                >
                                  View Response
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {completedEvals.filter(evaluation => evaluation.evaluationType === 'survey').length > 0 && (
                  <div className="mb-4">
                    <h5 className="mb-3">Survey Evaluations</h5>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th className="px-4">Title</th>
                          <th>Resident</th>
                          <th>Submitted On</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedEvals
                          .filter(evaluation => evaluation.evaluationType === 'survey')
                          .map((evaluation) => (
                            <tr key={evaluation.id}>
                              <td className="px-4">{evaluation.title}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <UserAvatar user={evaluation.resident} size={32} alt="" />
                                  <span>
                                    {evaluation.resident?.firstName} {evaluation.resident?.lastName}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {evaluation.submittedAt
                                  ? new Date(evaluation.submittedAt).toLocaleString()
                                  : "Date not available"}
                              </td>
                              <td>
                                <Button
                                  variant="primary"
                                  onClick={() => navigate(`/attending/completed-evaluation/${evaluation.responseId}`)}
                                >
                                  View Response
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {completedEvals.filter(evaluation => evaluation.evaluationType === 'questionnaire').length > 0 && (
                  <div className="mb-4">
                    <h5 className="mb-3">Questionnaire Evaluations</h5>
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th className="px-4">Title</th>
                          <th>Resident</th>
                          <th>Submitted On</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completedEvals
                          .filter(evaluation => evaluation.evaluationType === 'questionnaire')
                          .map((evaluation) => (
                            <tr key={evaluation.id}>
                              <td className="px-4">{evaluation.title}</td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <UserAvatar user={evaluation.resident} size={32} alt="" />
                                  <span>
                                    {evaluation.resident?.firstName} {evaluation.resident?.lastName}
                                  </span>
                                </div>
                              </td>
                              <td>
                                {evaluation.submittedAt
                                  ? new Date(evaluation.submittedAt).toLocaleString()
                                  : "Date not available"}
                              </td>
                              <td>
                                <Button
                                  variant="primary"
                                  onClick={() => navigate(`/attending/completed-evaluation/${evaluation.responseId}`)}
                                >
                                  View Response
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Container>
      
      <RecurringEvaluationsModal
        show={showRecurringModal}
        handleClose={() => {
          setShowRecurringModal(false);
          setSelectedRecurringEvaluation(null);
        }}
        evaluation={selectedRecurringEvaluation}
      />
    </div>
  );
};

export default AttendingEvaluations; 